import { logger } from './logger';

// Cache utility for better performance
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
const SESSION_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
const MAX_CACHE_SIZE_MB = 4; // Maximum cache size in MB before cleanup

// Helper functions for cache management

const checkStorageQuota = () => {
  const info = cacheUtils.getStorageInfo();
  return {
    isNearLimit: parseFloat(info.totalSizeMB) > MAX_CACHE_SIZE_MB,
    currentSizeMB: info.totalSizeMB,
    maxSizeMB: MAX_CACHE_SIZE_MB
  };
};

export const cacheUtils = {
  set: (key, data, ttl = CACHE_TTL) => {
    // Check if we're approaching storage limits
    const quotaInfo = checkStorageQuota();
    if (quotaInfo.isNearLimit) {
      console.warn(`Storage is near limit (${quotaInfo.currentSizeMB}MB). Clearing expired entries.`);
      cacheUtils.clearExpiredEntries();
    }

    const cacheData = {
      data,
      timestamp: Date.now(),
      ttl
    };
    
    try {
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn(`Storage quota exceeded for key: ${key}. Attempting to clear old cache entries.`);
        
        // Try to clear expired entries first
        cacheUtils.clearExpiredEntries();
        
        // Try again after clearing expired entries
        try {
          localStorage.setItem(key, JSON.stringify(cacheData));
          logger.log(`Successfully stored ${key} after clearing expired entries.`);
        } catch (retryError) {
          console.error(`Still unable to store ${key} after clearing expired entries. Data too large.`);
          
          // If still failing, try to clear all fleet planning cache for this user
          const userId = key.includes('_user_') ? key.split('_user_')[1] : null;
          if (userId) {
            cacheUtils.clearUserSpecificCache(userId);
            logger.log(`Cleared all cache for user ${userId} due to quota exceeded.`);
          }
          
          // Final attempt
          try {
            localStorage.setItem(key, JSON.stringify(cacheData));
          } catch (finalError) {
            console.error(`Failed to store ${key} even after clearing all user cache. Data may be too large for localStorage.`);
            // Don't throw the error - let the app continue without caching
          }
        }
      } else {
        console.error(`Error storing cache for key ${key}:`, error);
      }
    }
  },

  get: (key) => {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const cacheData = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is still valid
      if (now - cacheData.timestamp < cacheData.ttl) {
        return cacheData.data;
      } else {
        // Cache expired, remove it
        localStorage.removeItem(key);
        return null;
      }
    } catch (error) {
      console.error('Error reading from cache:', error);
      localStorage.removeItem(key);
      return null;
    }
  },

  clear: (key) => {
    localStorage.removeItem(key);
  },

  clearAll: (prefix = '') => {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  },

  // Clear all fleet planning related cache
  clearFleetPlanningCache: () => {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('fleet_planning_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    logger.log('Fleet planning cache cleared');
  },

  // Clear all user-specific cache for a given user
  clearUserSpecificCache: (userId) => {
    if (!userId) {
      console.warn('clearUserSpecificCache called without userId');
      return;
    }
    
    const keysToRemove = [];
    const userPrefix = `_user_${userId}`;
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.includes(userPrefix)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    logger.log(`User-specific cache cleared for user ${userId}`);
  },

  // Check if cache exists and is valid
  has: (key) => {
    return cacheUtils.get(key) !== null;
  },

  // Get cache info (for debugging)
  getInfo: (key) => {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const cacheData = JSON.parse(cached);
      const now = Date.now();
      const age = now - cacheData.timestamp;
      const isValid = age < cacheData.ttl;
      
      return {
        exists: true,
        age: age,
        ttl: cacheData.ttl,
        isValid: isValid,
        expiresIn: cacheData.ttl - age
      };
    } catch (error) {
      return null;
    }
  },

  // Session storage for immediate navigation (cleared on page refresh)
  setSession: (key, data, ttl = SESSION_CACHE_TTL) => {
    sessionStorage.setItem(key, JSON.stringify({
      data,
      timestamp: Date.now(),
      ttl
    }));
  },

  getSession: (key) => {
    try {
      const cached = sessionStorage.getItem(key);
      if (!cached) return null;

      const cacheData = JSON.parse(cached);
      const hasTimestamp = Number.isFinite(cacheData?.timestamp);
      const ttl = Number.isFinite(cacheData?.ttl) ? cacheData.ttl : SESSION_CACHE_TTL;

      if (hasTimestamp && Date.now() - cacheData.timestamp >= ttl) {
        sessionStorage.removeItem(key);
        return null;
      }

      return cacheData?.data ?? null;
    } catch (error) {
      console.error('Error reading from session cache:', error);
      sessionStorage.removeItem(key);
      return null;
    }
  },

  clearSession: (key) => {
    sessionStorage.removeItem(key);
  },

  // Clear all expired cache entries to free up space
  clearExpiredEntries: () => {
    const keysToRemove = [];
    const now = Date.now();
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('fleet_planning_')) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const cacheData = JSON.parse(cached);
            if (now - cacheData.timestamp >= cacheData.ttl) {
              keysToRemove.push(key);
            }
          }
        } catch (error) {
          // If we can't parse the data, remove it
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      logger.log(`Removed expired cache entry: ${key}`);
    });
    
    logger.log(`Cleared ${keysToRemove.length} expired cache entries`);
    return keysToRemove.length;
  },

  // Get storage usage information for debugging
  getStorageInfo: () => {
    let totalSize = 0;
    const entries = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key);
        const size = new Blob([value]).size;
        totalSize += size;
        entries.push({ key, size });
      }
    }
    
    return {
      totalSize,
      totalSizeMB: (totalSize / (1024 * 1024)).toFixed(2),
      entryCount: entries.length,
      entries: entries.sort((a, b) => b.size - a.size)
    };
  },

  // Proactively clean up cache when it gets too large
  cleanupIfNeeded: () => {
    const quotaInfo = checkStorageQuota();
    if (quotaInfo.isNearLimit) {
      logger.log(`Cache cleanup triggered. Current size: ${quotaInfo.currentSizeMB}MB`);
      
      // First, clear expired entries
      const expiredCleared = cacheUtils.clearExpiredEntries();
      
      // If still near limit, clear oldest entries
      const info = cacheUtils.getStorageInfo();
      if (parseFloat(info.totalSizeMB) > MAX_CACHE_SIZE_MB) {
        logger.log('Still near limit after clearing expired entries. Clearing oldest entries.');
        cacheUtils.clearOldestEntries();
      }
      
      return true;
    }
    return false;
  },

  // Clear oldest cache entries to free up space
  clearOldestEntries: () => {
    const entries = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('fleet_planning_')) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const cacheData = JSON.parse(cached);
            entries.push({
              key,
              timestamp: cacheData.timestamp,
              size: new Blob([cached]).size
            });
          }
        } catch (error) {
          // If we can't parse the data, remove it
          localStorage.removeItem(key);
        }
      }
    }
    
    // Sort by timestamp (oldest first)
    entries.sort((a, b) => a.timestamp - b.timestamp);
    
    // Remove oldest entries until we're under the limit
    let removedCount = 0;
    let removedSize = 0;
    
    for (const entry of entries) {
      localStorage.removeItem(entry.key);
      removedCount++;
      removedSize += entry.size;
      
      // Check if we've freed up enough space
      const currentInfo = cacheUtils.getStorageInfo();
      if (parseFloat(currentInfo.totalSizeMB) <= MAX_CACHE_SIZE_MB * 0.8) {
        break;
      }
    }
    
    logger.log(`Cleared ${removedCount} oldest cache entries, freed ${(removedSize/1024).toFixed(1)}KB`);
    return { removedCount, removedSize };
  }
};

// Cache keys for different data types
export const CACHE_KEYS = {
  ROUTES: 'fleet_planning_routes',
  DEPOTS: 'fleet_planning_depots',
  CHARGING_STATIONS: 'fleet_planning_charging_stations',
  TRUCKS: 'fleet_planning_trucks',
  CUSTOM_TRUCKS: 'fleet_planning_custom_trucks',
  VEHICLES: 'fleet_planning_vehicles',  // Alias for TRUCKS for consistency
  CUSTOM_VEHICLES: 'fleet_planning_custom_vehicles',  // Alias for CUSTOM_TRUCKS for consistency
  CIRCUITS: 'fleet_planning_circuits',
  LATEST_OPTIMIZATION: 'fleet_planning_latest_optimization',
  OPTIMIZATION_HISTORY: 'fleet_planning_optimization_history'
};

// Helper function to create user-specific cache keys
export const getUserSpecificCacheKey = (baseKey, userId) => {
  if (!userId) {
    console.warn('getUserSpecificCacheKey called without userId, using base key');
    return baseKey;
  }
  return `${baseKey}_user_${userId}`;
};

// Session storage keys
export const SESSION_KEYS = {
  LATEST_OPTIMIZATION: 'session_latest_optimization',
  OPTIMIZATION_HISTORY: 'session_optimization_history'
};

// Helper function to create a cached API call
export const createCachedApiCall = (apiCall, cacheKey, ttl = CACHE_TTL) => {
  return async (forceRefresh = false, ...args) => {
    // Check cache first (unless force refresh is requested)
    if (!forceRefresh) {
      const cachedData = cacheUtils.get(cacheKey);
      if (cachedData) {
        logger.log(`Using cached data for ${cacheKey}`);
        return cachedData;
      }
    }

    // Make the API call
    const data = await apiCall(...args);
    
    // Cache the result
    cacheUtils.set(cacheKey, data, ttl);
    
    return data;
  };
};

// Specialized optimization caching functions
export const optimizationCache = {
  // Cache latest optimization results
  setLatestOptimization: (data, userId) => {
    if (!userId) {
      console.warn('setLatestOptimization called without userId');
      return;
    }
    
    // Create user-specific keys
    const userSessionKey = `${SESSION_KEYS.LATEST_OPTIMIZATION}_user_${userId}`;
    const userCacheKey = getUserSpecificCacheKey(CACHE_KEYS.LATEST_OPTIMIZATION, userId);
    
    // Store only in session to avoid persisting large payloads in localStorage.
    cacheUtils.setSession(userSessionKey, data);
    cacheUtils.clear(userCacheKey);
  },

  getLatestOptimization: (userId) => {
    if (!userId) {
      console.warn('getLatestOptimization called without userId');
      return null;
    }
    
    // Create user-specific keys
    const userSessionKey = `${SESSION_KEYS.LATEST_OPTIMIZATION}_user_${userId}`;
    const userCacheKey = getUserSpecificCacheKey(CACHE_KEYS.LATEST_OPTIMIZATION, userId);
    
    // Session-only cache: keep responses in-memory for current browser session.
    const sessionData = cacheUtils.getSession(userSessionKey);
    if (sessionData) {
      return sessionData;
    }

    // Cleanup legacy persistent payload if it exists from previous versions.
    cacheUtils.clear(userCacheKey);
    return null;
  },

  // Cache optimization history
  setOptimizationHistory: (data, userId) => {
    if (!userId) {
      console.warn('setOptimizationHistory called without userId');
      return;
    }
    
    // Create user-specific keys
    const userSessionKey = `${SESSION_KEYS.OPTIMIZATION_HISTORY}_user_${userId}`;
    const userCacheKey = getUserSpecificCacheKey(CACHE_KEYS.OPTIMIZATION_HISTORY, userId);
    
    cacheUtils.setSession(userSessionKey, data);
    cacheUtils.clear(userCacheKey);
  },

  getOptimizationHistory: (userId) => {
    if (!userId) {
      console.warn('getOptimizationHistory called without userId');
      return null;
    }
    
    // Create user-specific keys
    const userSessionKey = `${SESSION_KEYS.OPTIMIZATION_HISTORY}_user_${userId}`;
    const userCacheKey = getUserSpecificCacheKey(CACHE_KEYS.OPTIMIZATION_HISTORY, userId);
    
    const sessionData = cacheUtils.getSession(userSessionKey);
    if (sessionData) {
      return sessionData;
    }

    // Cleanup legacy persistent payload if it exists from previous versions.
    cacheUtils.clear(userCacheKey);
    return null;
  },

  // Clear all optimization cache for a specific user
  clearOptimizationCache: (userId) => {
    if (!userId) {
      console.warn('clearOptimizationCache called without userId');
      return;
    }
    
    // Create user-specific keys
    const userSessionKeyLatest = `${SESSION_KEYS.LATEST_OPTIMIZATION}_user_${userId}`;
    const userSessionKeyHistory = `${SESSION_KEYS.OPTIMIZATION_HISTORY}_user_${userId}`;
    const userCacheKeyLatest = getUserSpecificCacheKey(CACHE_KEYS.LATEST_OPTIMIZATION, userId);
    const userCacheKeyHistory = getUserSpecificCacheKey(CACHE_KEYS.OPTIMIZATION_HISTORY, userId);
    
    cacheUtils.clear(userCacheKeyLatest);
    cacheUtils.clear(userCacheKeyHistory);
    cacheUtils.clearSession(userSessionKeyLatest);
    cacheUtils.clearSession(userSessionKeyHistory);
    logger.log(`Optimization cache cleared for user ${userId}`);
  },

  // Check if we have cached optimization data for a specific user
  hasOptimizationData: (userId) => {
    if (!userId) {
      console.warn('hasOptimizationData called without userId');
      return false;
    }
    
    return optimizationCache.getLatestOptimization(userId) !== null;
  }
};

// Global function for debugging and user control
if (typeof window !== 'undefined') {
  window.clearOptimizationCache = (userId) => {
    if (!userId) {
      console.warn('clearOptimizationCache requires userId parameter');
      return;
    }
    optimizationCache.clearOptimizationCache(userId);
    logger.log(`Optimization cache cleared for user ${userId} via global function`);
  };
  
  window.getOptimizationCacheInfo = (userId) => {
    if (!userId) {
      console.warn('getOptimizationCacheInfo requires userId parameter');
      return null;
    }
    
    const userCacheKeyLatest = getUserSpecificCacheKey(CACHE_KEYS.LATEST_OPTIMIZATION, userId);
    const userCacheKeyHistory = getUserSpecificCacheKey(CACHE_KEYS.OPTIMIZATION_HISTORY, userId);
    const userSessionKeyLatest = `${SESSION_KEYS.LATEST_OPTIMIZATION}_user_${userId}`;
    const userSessionKeyHistory = `${SESSION_KEYS.OPTIMIZATION_HISTORY}_user_${userId}`;
    
    return {
      latestOptimization: cacheUtils.getInfo(userCacheKeyLatest),
      optimizationHistory: cacheUtils.getInfo(userCacheKeyHistory),
      sessionLatest: cacheUtils.getSession(userSessionKeyLatest) ? 'exists' : 'not found',
      sessionHistory: cacheUtils.getSession(userSessionKeyHistory) ? 'exists' : 'not found'
    };
  };

  // Additional debugging functions for storage quota issues
  window.getStorageInfo = () => {
    const info = cacheUtils.getStorageInfo();
    logger.log('Storage Info:', info);
    return info;
  };

  window.clearExpiredCache = () => {
    const cleared = cacheUtils.clearExpiredEntries();
    logger.log(`Cleared ${cleared} expired cache entries`);
    return cleared;
  };

  window.clearAllFleetPlanningCache = () => {
    cacheUtils.clearFleetPlanningCache();
    logger.log('All fleet planning cache cleared');
  };

  window.clearUserCache = (userId) => {
    if (!userId) {
      console.warn('clearUserCache requires userId parameter');
      return;
    }
    cacheUtils.clearUserSpecificCache(userId);
    logger.log(`All cache cleared for user ${userId}`);
  };

  // Additional debugging functions for storage quota issues
  window.getStorageInfo = () => {
    const info = cacheUtils.getStorageInfo();
    logger.log('Storage Info:', info);
    return info;
  };

  window.clearExpiredCache = () => {
    const cleared = cacheUtils.clearExpiredEntries();
    logger.log(`Cleared ${cleared} expired cache entries`);
    return cleared;
  };

  window.clearAllFleetPlanningCache = () => {
    cacheUtils.clearFleetPlanningCache();
    logger.log('All fleet planning cache cleared');
  };

  window.cleanupCache = () => {
    const cleaned = cacheUtils.cleanupIfNeeded();
    logger.log(`Cache cleanup ${cleaned ? 'performed' : 'not needed'}`);
    return cleaned;
  };

  window.clearOldestCache = () => {
    const result = cacheUtils.clearOldestEntries();
    logger.log(`Cleared oldest cache entries:`, result);
    return result;
  };
} 